import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';

import {
  STREAM_ROTATED,
  streamAgentUserEvents,
  type AgentUserEvent,
} from '@/services/agents/user-events-stream';

interface UseUserEventsOptions {
  orgId?: string;
  agentId: string;
  enabled: boolean;
  /** Called each time the stream opens, including after a reconnect. */
  onOpen?: () => void;
  onEvent: (event: AgentUserEvent) => void;
}

const RECONNECT_MIN_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

/**
 * Keeps one per-user event stream open for as long as the chat is mounted:
 * every run the user owns pushes what it needs from them here, whichever
 * chat started it. Reconnects with backoff; the caller refetches on
 * reconnect so a dropped event is only ever late, never lost.
 */
export function useUserEvents({
  orgId,
  agentId,
  enabled,
  onOpen,
  onEvent,
}: UseUserEventsOptions): void {
  const onEventRef = useRef(onEvent);
  const onOpenRef = useRef(onOpen);
  useEffect(() => {
    onEventRef.current = onEvent;
    onOpenRef.current = onOpen;
  }, [onEvent, onOpen]);

  useEffect(() => {
    if (!enabled || !orgId || !agentId) return;
    const controller = new AbortController();
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;

    const connect = () => {
      if (stopped) return;
      void streamAgentUserEvents({
        orgId,
        agentId,
        signal: controller.signal,
        onOpen: () => onOpenRef.current?.(),
        onEvent: (event) => {
          attempt = 0;
          onEventRef.current(event);
        },
        onError: (error) => {
          if (stopped) return;
          if (error === STREAM_ROTATED) {
            attempt = 0;
            timer = setTimeout(connect, 0);
            return;
          }
          if (attempt === 0) {
            Sentry.captureMessage(`User event stream dropped: ${error}`, {
              level: 'info',
              extra: { orgId, agentId },
            });
          }
          const delay = Math.min(
            RECONNECT_MAX_MS,
            RECONNECT_MIN_MS * 2 ** attempt,
          );
          attempt += 1;
          timer = setTimeout(connect, delay);
        },
      });
    };

    connect();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      controller.abort();
    };
  }, [enabled, orgId, agentId]);
}
